import type { ReportRegistryEntry } from '@/types/report';

// IMPORTANT: Field names in `columns` are based on the integration plan doc + backend repo verification.
// Before finalising a column config, cross-check the actual field names against the matching
// Repository class in the sibling lokaroom repo (app/Repositories/Reports/*.php).
// Known verified discrepancy: sales.daily uses `room_revenue` NOT `accommodation`.

export const REPORT_REGISTRY: ReportRegistryEntry[] = [

    // ─── Occupancy ────────────────────────────────────────────────────────────

    {
        reportKey: 'occupancy',
        reportType: 'occupancy',
        period: 'daily',
        label: 'Occupancy',
        groupLabel: 'Performance',
        featureKey: 'report_occupancy_daily',
        featureKeys: ['report_occupancy_daily', 'report_occupancy_monthly'],
        family: 'flat',
        filterFields: [],
        defaultPeriodMode: 'date_range',
        periodModes: [
            {
                value: 'yearly',
                label: 'Yearly',
                period: 'monthly',
                family: 'pivot',
                featureKey: 'report_occupancy_monthly',
                filterFields: [
                    { key: 'year', label: 'Year', type: 'year', required: true, defaultValue: new Date().getFullYear().toString() },
                    { key: 'room_type_ids', label: 'Room Types', type: 'multiselect-roomtype', required: false },
                ],
                pivotLabelField: 'room_type_name',
                pivotLabelHeader: 'Room Type',
                pivotMetricKey: 'occupancy_percent',
                pivotMetricFormat: 'percent',
            },
            {
                value: 'date_range',
                label: 'Date Range',
                period: 'daily',
                family: 'flat',
                featureKey: 'report_occupancy_daily',
                filterFields: [
                    { key: 'daterange', label: 'Date Range', type: 'daterange', required: true },
                    { key: 'room_type_ids', label: 'Room Types', type: 'multiselect-roomtype', required: false },
                ],
                columns: [
                    { key: 'date', label: 'Date', format: 'date' },
                    { key: 'rooms', label: 'Total Rooms', format: 'number' },
                    { key: 'rooms_sold', label: 'Rooms Sold', format: 'number' },
                    { key: 'available', label: 'Available', format: 'number' },
                    { key: 'occupancy_percent', label: 'Occ %', format: 'percent' },
                ],
            },
        ],
    },

    // ─── Night Audit Summary ──────────────────────────────────────────────────

    {
        reportKey: 'night-audit-summary-daily',
        reportType: 'night_audit',
        period: 'daily',
        label: 'Summary Night Audit',
        groupLabel: 'Performance',
        featureKey: 'report_night_audit',
        family: 'night-audit-summary',
        filterFields: [
            {
                key: 'daterange',
                label: 'Date Range',
                type: 'daterange',
                required: true,
                disableFuture: true,
            },
        ],
    },

    {
        reportKey: 'daily-live-today',
        reportType: 'daily',
        period: 'live',
        label: 'Daily Statistics (Today)',
        groupLabel: 'Operations',
        featureKey: 'report_night_audit',
        family: 'daily-live',
        filterFields: [
            { key: 'date', label: 'Date', type: 'date', required: true, disableFuture: true },
        ],
    },

    // ─── Revenue ──────────────────────────────────────────────────────────────

    {
        reportKey: 'revenue',
        reportType: 'revenue',
        period: 'daily',
        label: 'Revenue',
        groupLabel: 'Performance',
        featureKey: 'report_revenue_daily',
        featureKeys: ['report_revenue_daily', 'report_revenue_monthly'],
        family: 'flat',
        filterFields: [],
        defaultPeriodMode: 'date_range',
        periodModes: [
            {
                value: 'yearly',
                label: 'Yearly',
                period: 'monthly',
                family: 'pivot',
                featureKey: 'report_revenue_monthly',
                filterFields: [
                    { key: 'year', label: 'Year', type: 'year', required: true, defaultValue: new Date().getFullYear().toString() },
                    { key: 'room_type_ids', label: 'Room Types', type: 'multiselect-roomtype', required: false },
                    { key: 'with_extra', label: 'With Extra Costs', type: 'select', required: false, options: [{ value: true, label: 'Include Extra' }, { value: false, label: 'Exclude Extra' }] },
                    { key: 'currency', label: 'Currency Code', type: 'text', required: false, placeholder: 'e.g. IDR' },
                ],
                pivotLabelField: 'room_type_name',
                pivotLabelHeader: 'Room Type',
                pivotMetricKey: 'revenue',
                pivotMetricFormat: 'currency',
            },
            {
                value: 'date_range',
                label: 'Date Range',
                period: 'daily',
                family: 'flat',
                featureKey: 'report_revenue_daily',
                filterFields: [
                    { key: 'daterange', label: 'Date Range', type: 'daterange', required: true },
                    { key: 'room_type_ids', label: 'Room Types', type: 'multiselect-roomtype', required: false },
                    { key: 'with_extra', label: 'With Extra Costs', type: 'select', required: false, options: [{ value: true, label: 'Include Extra' }, { value: false, label: 'Exclude Extra' }] },
                    { key: 'currency', label: 'Currency Code', type: 'text', required: false, placeholder: 'e.g. IDR' },
                ],
                columns: [
                    { key: 'date', label: 'Date', format: 'date' },
                    { key: 'total_rooms', label: 'Rooms', format: 'number' },
                    { key: 'rooms_sold', label: 'Sold', format: 'number' },
                    { key: 'occupancy_percent', label: 'Occ %', format: 'percent' },
                    { key: 'room_revenue', label: 'Room Revenue', format: 'currency' },
                    { key: 'extra_costs', label: 'Extra', format: 'currency' },
                    { key: 'adr', label: 'ADR', format: 'currency' },
                    { key: 'revpar', label: 'RevPAR', format: 'currency' },
                ],
            },
        ],
    },

    // ─── Agent ────────────────────────────────────────────────────────────────

    {
        reportKey: 'agent',
        reportType: 'agent',
        period: 'daily',
        label: 'Agent',
        groupLabel: 'Sales & Channel',
        featureKey: 'report_agent_daily',
        featureKeys: ['report_agent_daily', 'report_agent_monthly'],
        family: 'flat',
        filterFields: [],
        defaultPeriodMode: 'date_range',
        periodModes: [
            {
                value: 'yearly',
                label: 'Yearly',
                period: 'monthly',
                family: 'pivot',
                featureKey: 'report_agent_monthly',
                filterFields: [
                    { key: 'year', label: 'Year', type: 'year', required: true, defaultValue: new Date().getFullYear().toString() },
                    {
                        key: 'based_on', label: 'Based On', type: 'select', required: true,
                        defaultValue: 'stay_dates',
                        options: [
                            { value: 'stay_dates', label: 'Stay Dates' },
                            { value: 'created_reservation', label: 'Created Reservation' },
                        ],
                    },
                    { key: 'room_type_ids', label: 'Room Types', type: 'multiselect-roomtype', required: false },
                ],
                pivotLabelField: 'agent_name',
                pivotLabelHeader: 'Agent / Channel',
                pivotMetricKey: 'nights_booked',
                pivotMetricFormat: 'number',
            },
            {
                value: 'date_range',
                label: 'Date Range',
                period: 'daily',
                family: 'flat',
                featureKey: 'report_agent_daily',
                filterFields: [
                    { key: 'daterange', label: 'Date Range', type: 'daterange', required: true },
                    {
                        key: 'based_on', label: 'Based On', type: 'select', required: true,
                        defaultValue: 'stay_dates',
                        options: [
                            { value: 'stay_dates', label: 'Stay Dates' },
                            { value: 'created_reservation', label: 'Created Reservation' },
                        ],
                    },
                ],
                columns: [
                    { key: 'agent_name', label: 'Agent / Channel', format: 'text' },
                    { key: 'nb_reservations', label: 'Reservations', format: 'number' },
                    { key: 'nb_nights', label: 'Nights', format: 'number' },
                    { key: 'avg_length_of_stay', label: 'Avg LoS', format: 'number' },
                    { key: 'avg_earning', label: 'Avg Rate', format: 'currency' },
                    { key: 'total_earning', label: 'Total Earning', format: 'currency' },
                    { key: 'percentage', label: 'Share %', format: 'percent' },
                ],
            },
        ],
    },

    // ─── Extra ────────────────────────────────────────────────────────────────

    {
        reportKey: 'extra-daily',
        reportType: 'extra',
        period: 'daily',
        label: 'Extra',
        groupLabel: 'Sales & Channel',
        featureKey: 'report_extra_daily',
        menuHidden: true,
        family: 'flat',
        filterFields: [
            { key: 'daterange', label: 'Date Range', type: 'daterange', required: true },
            {
                key: 'based_on', label: 'Based On', type: 'select', required: true,
                options: [
                    { value: 'check_in_dates', label: 'Check-in Dates' },
                    { value: 'extra_date', label: 'Extra Date' },
                ],
            },
            { key: 'room_type_ids', label: 'Room Types', type: 'multiselect-roomtype', required: false },
        ],
        columns: [
            { key: 'booking_no', label: 'Booking No', format: 'text' },
            { key: 'guest_name', label: 'Guest', format: 'text' },
            { key: 'extra_name', label: 'Extra Item', format: 'text' },
            { key: 'amount', label: 'Amount', format: 'currency' },
        ],
    },

    // ─── Sales ────────────────────────────────────────────────────────────────

    {
        reportKey: 'sales-daily',
        reportType: 'sales',
        period: 'daily',
        label: 'Sales (Actual)',
        groupLabel: 'Sales & Channel',
        featureKey: 'report_sales_daily',
        family: 'flat',
        filterFields: [
            { key: 'daterange', label: 'Date Range', type: 'daterange', required: true },
            {
                key: 'based_on', label: 'Based On', type: 'select', required: true,
                defaultValue: 'stay_dates',
                options: [
                    { value: 'check_in_dates', label: 'Check-in Dates' },
                    { value: 'check_out_dates', label: 'Check-out Dates' },
                    { value: 'created_reservation', label: 'Created Reservation' },
                    { value: 'stay_dates', label: 'Stay Dates' },
                ],
            },
            { key: 'room_ids', label: 'Rooms', type: 'multiselect-room', required: false },
        ],
        // VERIFIED against SalesReportRepository.php — field is `room_revenue` not `accommodation`
        // Extra fields: booking_date, booking_reference, country, payment_type, ota_commission, ota_taxes
        columns: [
            { key: 'booking_no', label: 'Booking No', format: 'text' },
            { key: 'booking_reference', label: 'Booking Ref', format: 'text' },
            { key: 'booking_date', label: 'Booking Date', format: 'date' },
            { key: 'guest_name', label: 'Guest', format: 'text' },
            { key: 'room_type', label: 'Room Type', format: 'text' },
            { key: 'room_no', label: 'Room No', format: 'text' },
            { key: 'check_in', label: 'Check-in', format: 'date' },
            { key: 'check_out', label: 'Check-out', format: 'date' },
            { key: 'nights', label: 'Nights', format: 'number' },
            { key: 'source', label: 'Source', format: 'text' },
            { key: 'room_revenue', label: 'Room Revenue', format: 'currency' },
            { key: 'extras', label: 'Extras', format: 'currency' },
            { key: 'ota_commission', label: 'OTA Commission', format: 'currency' },
            { key: 'total', label: 'Total', format: 'currency' },
        ],
    },

    // ─── Payment ──────────────────────────────────────────────────────────────

    {
        reportKey: 'payment-daily',
        reportType: 'payment',
        period: 'daily',
        label: 'Payment by Method',
        groupLabel: 'Payments',
        featureKey: 'report_payment_daily',
        family: 'payment-method',
        filterFields: [
            { key: 'daterange', label: 'Date Range', type: 'daterange', required: true },
            { key: 'room_ids', label: 'Rooms', type: 'multiselect-room', required: false },
        ],
        columns: [
            { key: 'date', label: 'Date', format: 'date' },
            { key: 'booking_no', label: 'Booking No', format: 'text' },
            { key: 'booking_reference', label: 'Booking Ref', format: 'text' },
            { key: 'guest_name', label: 'Guest', format: 'text' },
            { key: 'room_type', label: 'Room Type', format: 'text' },
            { key: 'room_no', label: 'Room', format: 'text' },
            { key: 'check_in', label: 'Check-in', format: 'date' },
            { key: 'source', label: 'Source', format: 'text' },
            { key: 'payment_type', label: 'Payment Method', format: 'text' },
            { key: 'amount', label: 'Amount', format: 'currency' },
            { key: 'added', label: 'Added At', format: 'date' },
            { key: 'user_name', label: 'Recorded By', format: 'text' },
        ],
    },

    // ─── Country ──────────────────────────────────────────────────────────────

    {
        reportKey: 'country-daily',
        reportType: 'country',
        period: 'daily',
        label: 'Country / Guest Statistics',
        groupLabel: 'Operations',
        featureKey: 'report_country_daily',
        family: 'flat',
        filterFields: [
            { key: 'daterange', label: 'Date Range', type: 'daterange', required: true },
            {
                key: 'based_on', label: 'Based On', type: 'select', required: true,
                options: [
                    { value: 'check_in_dates', label: 'Check-in Dates' },
                    { value: 'created_reservation', label: 'Created Reservation' },
                ],
            },
            {
                key: 'based_on_2', label: 'Group By', type: 'select', required: true,
                options: [
                    { value: 'country', label: 'Country' },
                    // state/city not implemented in CountryReportRepository yet
                ],
            },
            { key: 'room_ids', label: 'Rooms', type: 'multiselect-room', required: false },
        ],
        columns: [
            { key: 'group', label: 'Country', format: 'text' },
            { key: 'nb_reservations', label: 'Reservations', format: 'number' },
            { key: 'amount', label: 'Total Revenue', format: 'currency' },
        ],
    },

    // ─── Daily ────────────────────────────────────────────────────────────────

    {
        reportKey: 'daily-day',
        reportType: 'daily',
        period: 'day',
        label: 'Daily Statistics (Single Day)',
        groupLabel: 'Operations',
        featureKey: 'report_daily_day',
        family: 'flat',
        menuHidden: true,
        filterFields: [
            { key: 'date', label: 'Date', type: 'date', required: true },
            { key: 'room_ids', label: 'Rooms', type: 'multiselect-room', required: false },
        ],
        // VERIFIED: DailyReportRepository.getDayStats returns [{description, this_date, mtd, ytd}]
        // Values are mixed types per row (int, currency string, percent string) — use 'text' to avoid double-formatting
        columns: [
            { key: 'description', label: 'Metric', format: 'text' },
            { key: 'this_date', label: 'Today', format: 'text' },
            { key: 'mtd', label: 'Month to Date', format: 'text' },
            { key: 'ytd', label: 'Year to Date', format: 'text' },
        ],
    },

    {
        reportKey: 'daily-range',
        reportType: 'daily',
        period: 'range',
        label: 'Daily Statistics (Date Range)',
        groupLabel: 'Operations',
        featureKey: 'report_daily_range',
        family: 'flat',
        menuHidden: true,
        filterFields: [
            { key: 'daterange', label: 'Date Range', type: 'daterange', required: true },
            { key: 'room_ids', label: 'Rooms', type: 'multiselect-room', required: false },
        ],
        columns: [
            { key: 'date', label: 'Date', format: 'date' },
            { key: 'room_available', label: 'Rooms Avail', format: 'number' },
            { key: 'rooms_sold', label: 'Rooms Sold', format: 'number' },
            { key: 'ooo_rooms', label: 'OOO', format: 'number' },
            { key: 'room_revenue_total', label: 'Revenue', format: 'currency' },
            { key: 'arrival_rooms', label: 'Arrivals', format: 'number' },
            { key: 'departure_rooms', label: 'Departures', format: 'number' },
            { key: 'cancel_rooms', label: 'Cancel', format: 'number' },
            { key: 'no_show', label: 'No Show', format: 'number' },
        ],
    },

    // ─── Summary ──────────────────────────────────────────────────────────────

    {
        reportKey: 'summary-range',
        reportType: 'summary',
        period: 'range',
        label: 'Summary',
        groupLabel: 'Performance',
        featureKey: 'report_summary_range',
        family: 'summary-range',
        menuHidden: true,
        filterFields: [
            { key: 'daterange', label: 'Date Range', type: 'daterange', required: true },
            {
                key: 'group_by', label: 'Group By', type: 'select', required: true,
                options: [
                    { value: 'room_type', label: 'Room Type' },
                    { value: 'agent', label: 'Agent / Channel' },
                ],
            },
            { key: 'room_ids', label: 'Rooms', type: 'multiselect-room', required: false },
        ],
        // Response: { sections: [{ header, rows: [{label, room_nights_sold, reservations, revenue, avg_rate, other_charges, room_only, cancellation_count}], total }], grand_total }
    },

    // ─── Guest ────────────────────────────────────────────────────────────────

    {
        reportKey: 'guest-daily',
        reportType: 'guest',
        period: 'daily',
        label: 'Guest',
        groupLabel: 'Operations',
        featureKey: 'report_guest_daily',
        menuHidden: true,
        family: 'flat',
        filterFields: [
            { key: 'daterange', label: 'Date Range', type: 'daterange', required: true },
            { key: 'room_ids', label: 'Rooms', type: 'multiselect-room', required: false },
        ],
        columns: [
            { key: 'booking_no', label: 'Booking No', format: 'text' },
            { key: 'first_name', label: 'First Name', format: 'text' },
            { key: 'last_name', label: 'Last Name', format: 'text' },
            { key: 'gender', label: 'Gender', format: 'text' },
            { key: 'nationality', label: 'Nationality', format: 'text' },
            { key: 'id_type', label: 'ID Type', format: 'text' },
            { key: 'id_no', label: 'ID No', format: 'text' },
            { key: 'phone', label: 'Phone', format: 'text' },
            { key: 'email', label: 'Email', format: 'text' },
            { key: 'room', label: 'Room', format: 'text' },
            { key: 'check_in', label: 'Check-in', format: 'date' },
            { key: 'check_out', label: 'Check-out', format: 'date' },
            { key: 'adult', label: 'Adult', format: 'number' },
            { key: 'child', label: 'Child', format: 'number' },
            { key: 'status', label: 'Status', format: 'text' },
            { key: 'type_of_visa', label: 'Visa Type', format: 'text' },
            { key: 'date_of_issue', label: 'Date of Issue', format: 'date' },
            { key: 'expire_date_of_stay', label: 'Expire Date', format: 'date' },
        ],
    },

    // ─── Breakfast / Inclusion ────────────────────────────────────────────────

    {
        reportKey: 'breakfast-daily',
        reportType: 'breakfast',
        period: 'daily',
        label: 'Breakfast / Inclusion',
        groupLabel: 'Operations',
        featureKey: 'report_breakfast_daily',
        menuHidden: true,
        family: 'flat',
        filterFields: [
            { key: 'daterange', label: 'Date Range', type: 'daterange', required: true },
            {
                key: 'based_on', label: 'Based On', type: 'select', required: true,
                options: [
                    { value: 'rateplans', label: 'Rate Plans' },
                    { value: 'values', label: 'Values' },
                ],
            },
            { key: 'room_ids', label: 'Rooms', type: 'multiselect-room', required: false },
        ],
        columns: [
            { key: 'booking_no', label: 'Booking No', format: 'text' },
            { key: 'guest_name', label: 'Guest', format: 'text' },
            { key: 'rate_plan', label: 'Rate Plan', format: 'text' },
            { key: 'incl_days', label: 'Incl. Days', format: 'number' },
            { key: 'total_adults', label: 'Adults', format: 'number' },
            { key: 'total_children', label: 'Children', format: 'number' },
            { key: 'revenue_adjustment', label: 'Revenue Adj', format: 'currency' },
        ],
    },

    // ─── Arrival & Departure ─────────────────────────────────────────────────

    {
        reportKey: 'arrival-daily',
        reportType: 'arrival',
        period: 'daily',
        label: 'Arrival & Departure',
        groupLabel: 'Operations',
        featureKey: 'report_arrival_daily',
        menuHidden: true,
        family: 'flat',
        filterFields: [
            { key: 'daterange', label: 'Date Range', type: 'daterange', required: true },
            {
                key: 'mode', label: 'Mode', type: 'select', required: false,
                defaultValue: 'both',
                options: [
                    { value: 'arrival', label: 'Arrivals Only' },
                    { value: 'departure', label: 'Departures Only' },
                    { value: 'both', label: 'Both' },
                ],
            },
            { key: 'room_ids', label: 'Rooms', type: 'multiselect-room', required: false },
        ],
        columns: [
            { key: 'booking_no', label: 'Booking No', format: 'text' },
            { key: 'guest_name', label: 'Guest', format: 'text' },
            { key: 'room_no', label: 'Room', format: 'text' },
            { key: 'room_type', label: 'Type', format: 'text' },
            { key: 'check_in', label: 'Check-in', format: 'date' },
            { key: 'check_out', label: 'Check-out', format: 'date' },
            { key: 'nights', label: 'Nights', format: 'number' },
            { key: 'booking_status', label: 'Status', format: 'text' },
            { key: 'source', label: 'Source', format: 'text' },
        ],
    },

    // ─── In-House ─────────────────────────────────────────────────────────────

    {
        reportKey: 'inhouse-daily',
        reportType: 'inhouse',
        period: 'daily',
        label: 'In-House Guest',
        groupLabel: 'Operations',
        featureKey: 'report_inhouse_daily',
        menuHidden: true,
        family: 'flat',
        filterFields: [
            { key: 'date', label: 'Date', type: 'date', required: true },
            { key: 'room_ids', label: 'Rooms', type: 'multiselect-room', required: false },
        ],
        columns: [
            { key: 'booking_no', label: 'Booking No', format: 'text' },
            { key: 'guest_name', label: 'Guest', format: 'text' },
            { key: 'guest_status', label: 'Status', format: 'text' },
            { key: 'room_no', label: 'Room', format: 'text' },
            { key: 'room_type', label: 'Type', format: 'text' },
            { key: 'check_in', label: 'Check-in', format: 'date' },
            { key: 'check_out', label: 'Check-out', format: 'date' },
            { key: 'nights_remaining', label: 'Nights Left', format: 'number' },
            { key: 'source', label: 'Source', format: 'text' },
        ],
    },

    // ─── Cancellation ─────────────────────────────────────────────────────────

    {
        reportKey: 'cancellation-daily',
        reportType: 'cancellation',
        period: 'daily',
        label: 'Cancellation & No Show',
        groupLabel: 'Operations',
        featureKey: 'report_cancellation_daily',
        family: 'cancellation-noshow',
        filterFields: [
            { key: 'daterange', label: 'Date Range', type: 'daterange', required: true },
            {
                key: 'based_on', label: 'Based On', type: 'select', required: false,
                defaultValue: 'cancelled_date',
                options: [
                    { value: 'cancelled_date', label: 'Cancellation Date' },
                    { value: 'check_in_dates', label: 'Check-in Date' },
                ],
            },
            { key: 'room_ids', label: 'Rooms', type: 'multiselect-room', required: false },
        ],
        columns: [
            { key: 'booking_no', label: 'Booking No', format: 'text' },
            { key: 'guest_name', label: 'Guest', format: 'text' },
            { key: 'room_no', label: 'Room', format: 'text' },
            { key: 'room_type', label: 'Type', format: 'text' },
            { key: 'planned_check_in', label: 'Planned CI', format: 'date' },
            { key: 'planned_check_out', label: 'Planned CO', format: 'date' },
            { key: 'nights', label: 'Nights', format: 'number' },
            { key: 'status', label: 'Status', format: 'text' },
            { key: 'cancelled_at', label: 'Cancel Date', format: 'date' },
            { key: 'source', label: 'Source', format: 'text' },
            { key: 'potential_loss', label: 'Potential Loss', format: 'currency' },
        ],
    },

    // ─── Room Status ──────────────────────────────────────────────────────────

    {
        reportKey: 'roomstatus-daily',
        reportType: 'roomstatus',
        period: 'daily',
        label: 'Room Status',
        groupLabel: 'Operations',
        featureKey: 'report_roomstatus_daily',
        menuHidden: true,
        family: 'flat',
        filterFields: [
            { key: 'date', label: 'Date', type: 'date', required: true },
        ],
        columns: [
            { key: 'room_no', label: 'Room No', format: 'text' },
            { key: 'room_type', label: 'Type', format: 'text' },
            { key: 'status', label: 'Status', format: 'text' },
            { key: 'guest_name', label: 'Guest', format: 'text' },
            { key: 'booking_no', label: 'Booking No', format: 'text' },
            { key: 'check_out', label: 'Check-out', format: 'date' },
        ],
    },

    // ─── Tax ──────────────────────────────────────────────────────────────────

    {
        reportKey: 'tax-daily',
        reportType: 'tax',
        period: 'daily',
        label: 'Tax',
        groupLabel: 'Payments',
        featureKey: 'report_tax_daily',
        family: 'tax-daily',
        menuHidden: true,
        filterFields: [
            { key: 'daterange', label: 'Date Range', type: 'daterange', required: true },
            { key: 'room_ids', label: 'Rooms', type: 'multiselect-room', required: false },
        ],
    },

    // ─── Folio Balance ────────────────────────────────────────────────────────

    {
        reportKey: 'folio-range',
        reportType: 'folio',
        period: 'range',
        label: 'Folio Balance',
        groupLabel: 'Payments',
        featureKey: 'report_folio_range',
        menuHidden: true,
        family: 'flat',
        filterFields: [
            { key: 'daterange', label: 'Date Range', type: 'daterange', required: true },
            {
                key: 'based_on', label: 'Based On', type: 'select', required: false,
                defaultValue: 'check_in_dates',
                options: [
                    { value: 'check_in_dates', label: 'Check-in Dates' },
                    { value: 'check_out_dates', label: 'Check-out Dates' },
                    { value: 'created_reservation', label: 'Created Reservation' },
                ],
            },
            {
                key: 'filter', label: 'Filter', type: 'select', required: false,
                defaultValue: 'all',
                options: [
                    { value: 'all', label: 'All' },
                    { value: 'outstanding', label: 'Outstanding' },
                    { value: 'settled', label: 'Settled' },
                ],
            },
            { key: 'room_ids', label: 'Rooms', type: 'multiselect-room', required: false },
        ],
        columns: [
            { key: 'booking_no', label: 'Booking No', format: 'text' },
            { key: 'guest_name', label: 'Guest', format: 'text' },
            { key: 'room_no', label: 'Room', format: 'text' },
            { key: 'room_type', label: 'Type', format: 'text' },
            { key: 'check_in', label: 'Check-in', format: 'date' },
            { key: 'check_out', label: 'Check-out', format: 'date' },
            { key: 'booking_status', label: 'Status', format: 'text' },
            { key: 'total_billed', label: 'Total Billed', format: 'currency' },
            { key: 'total_paid', label: 'Total Paid', format: 'currency' },
            { key: 'balance', label: 'Balance', format: 'currency' },
        ],
    },

    // ─── Reservation Summary ──────────────────────────────────────────────────

    {
        reportKey: 'reservation-summary',
        reportType: 'reservation',
        period: 'summary',
        label: 'Reservation Summary',
        groupLabel: 'Operations',
        featureKey: 'report_reservation_summary',
        menuHidden: true,
        family: 'reservation-summary',
        filterFields: [
            { key: 'from_date', label: 'From Date', type: 'date', required: true },
            { key: 'upcoming_days', label: 'Upcoming Days', type: 'number', required: false, min: 1, max: 30, defaultValue: 7 },
        ],
    },

    // ─── Booking Modification ─────────────────────────────────────────────────

    {
        reportKey: 'modification-daily',
        reportType: 'modification',
        period: 'daily',
        label: 'Booking Modification',
        groupLabel: 'Operations',
        featureKey: 'report_modification_daily',
        menuHidden: true,
        family: 'flat',
        filterFields: [
            { key: 'daterange', label: 'Date Range', type: 'daterange', required: true },
        ],
        columns: [
            { key: 'booking_no', label: 'Booking No', format: 'text' },
            { key: 'guest_name', label: 'Guest', format: 'text' },
            { key: 'action', label: 'Action', format: 'text' },
            { key: 'value_before', label: 'Before', format: 'text' },
            { key: 'value_after', label: 'After', format: 'text' },
            { key: 'changed_by', label: 'Changed By', format: 'text' },
            { key: 'changed_at', label: 'Time', format: 'date' },
        ],
    },
];

export const REPORT_GROUPS = [
    'Operations',
    'Performance',
    'Sales & Channel',
    'Payments',
] as const;
